































import { Component, mixins, namespace } from 'nuxt-property-decorator';

import Dropdown, { DropdownMenu } from '@/components/common/Dropdown';
import HeaderMenuNotificationsList from '@/components/global/HeaderMenu/HeaderMenuNotifications/HeaderMenuNotificationsList/HeaderMenuNotificationsList.vue';
import HeaderMenuNotificationsToggler from '@/components/global/HeaderMenu/HeaderMenuNotifications/HeaderMenuNotificationsToggler/HeaderMenuNotificationsToggler.vue';

import HeaderMenuConnectionsMixin from '@/components/global/HeaderMenu/HeaderMenuConnections/HeaderMenuConnectionsMixin';

import clickOutside from '@/directives/click_outside';

import notificationsQuery from '@/apollo/queries/Notifications.query';
import currentUserUnseenNotificationsQuery from '@/components/global/HeaderMenu/HeaderMenuNotifications/CurrentUserUnseenNotifications.query';
import readNotificationsMutation from '@/apollo/mutations/ReadNotifications.mutation';
import viewNotifications from '@/apollo/mutations/ViewNotifications.mutation';

import type { DataProxy } from '@apollo/client/core';
import { NotificationKind, Platform } from '@/types/graphql';
import type {
  CurrentUser,
  NotificationConnection,
  ViewAllNotificationsMutation,
  ViewAllNotificationsMutationVariables,
} from '@/types/graphql';

const UIStore = namespace('ui');
const WEB_PLATFORM = Platform.Web;

@Component<HeaderMenuNotifications>({
  name: 'HeaderMenuNotifications',
  components: {
    Dropdown,
    DropdownMenu,
    HeaderMenuNotificationsList,
    HeaderMenuNotificationsToggler,
  },
  directives: {
    'click-outside': clickOutside,
  },
  // @ts-ignore
  apollo: {
    notifications: {
      query: notificationsQuery,
      skip(): boolean {
        // @ts-ignore
        return !this.isNotificationsDropdownOpen;
      },
      variables: {
        platform: WEB_PLATFORM,
      },
    },
    currentUser: {
      query: currentUserUnseenNotificationsQuery,
      variables: {
        platform: WEB_PLATFORM,
      },
    },
  },
  watch: {
    isNotificationsDropdownOpen(newVal: boolean): void {
      if (newVal) {
        if (this.hasUnseenNotifications) {
          this.markUnseenNotifications();
        }

        window.document.body.setAttribute('locked', '1');
      } else {
        window.document.body.removeAttribute('locked');
      }
    },
  },
})
export default class HeaderMenuNotifications extends mixins(HeaderMenuConnectionsMixin) {
  @UIStore.State('isNotificationsDropdownOpen') isNotificationsDropdownOpen!: boolean;
  @UIStore.Mutation('toggleNotificationsDropdown') toggleNotificationsDropdown!: (isOpen: boolean) => void;

  readonly notifications: NotificationConnection | null = null;
  readonly currentUser: CurrentUser | null = null;
  loadingMore = false;

  get loadingInitial(): boolean {
    // @ts-ignore
    return this.$apollo.queries.notifications.loading && !this.notifications;
  }

  get hasUnseenNotifications(): boolean {
    return !!this.currentUser?.unseenWarRoomNotifications;
  }

  get hasUnreadNotifications(): boolean {
    return !!this.currentUser?.unreadWarRoomNotifications;
  }

  get hasActionNeededNotifications(): boolean {
    return this.notifications?.edges.some(edge => edge.node?.kind === NotificationKind.ActionNeeded) || false;
  }

  async readAll(): Promise<void> {
    this.$analytics.logEvent('header__notifications__read-all-button__click');

    try {
      await Promise.all([
        this.readAllConnectionRequest(),
        this.$apollo.mutate({
          mutation: readNotificationsMutation,
          variables: { input: {} },
          update: (store: DataProxy) => {
            const data = store.readQuery<{ notifications: NotificationConnection }>({
              query: notificationsQuery,
              variables: { platform: WEB_PLATFORM },
            });

            if (!data?.notifications) return;

            const newData = {
              ...data,
              notifications: {
                ...data.notifications,
                edges: data.notifications.edges
                  .filter(e => e.node?.kind !== NotificationKind.ActionNeeded)
                  .map(e => ({ ...e, node: { ...e.node, readAt: new Date().toString() } })),
              },
            };

            store.writeQuery({ query: notificationsQuery, data: newData });
            this.updateCurrentUserLocal(store, { unreadWarRoomNotifications: false });
          },
        }),
      ]);
    } catch (e) {
      this.$notice.error(e);
      this.$sentryUtils.error(e);
    }
  }

  async showMore(): Promise<void> {
    if (!this.notifications) return;

    const { endCursor, hasNextPage } = this.notifications.pageInfo;

    if (!hasNextPage) return;

    this.loadingMore = true;

    try {
      // @ts-ignore
      await this.$apollo.queries.notifications.fetchMore({
        variables: {
          cursor: endCursor,
        },
        updateQuery: (
          { notifications }: { notifications: NotificationConnection },
          { fetchMoreResult }: { fetchMoreResult?: { notifications: NotificationConnection } },
        ) => {
          return {
            notifications: {
              ...notifications,
              pageInfo: {
                ...notifications.pageInfo,
                endCursor: fetchMoreResult?.notifications.pageInfo.endCursor,
                hasNextPage: fetchMoreResult?.notifications.pageInfo.hasNextPage,
              },

              edges: [...notifications.edges, ...(fetchMoreResult?.notifications.edges ?? [])],
            },
          };
        },
      });
    } catch (e) {
      this.$notice.error(e);
      this.$sentryUtils.error(e);
    } finally {
      this.loadingMore = false;
    }
  }

  handleClickOutside() {
    if (this.isNotificationsDropdownOpen) {
      requestAnimationFrame(() => {
        this.closeList();
      });
    }
  }

  closeList(): void {
    this.toggleNotificationsDropdown(false);
  }

  sendEventOnToggle(isOpened: boolean): void {
    this.$analytics.logEvent(`global__header-nav__notifications__click`, {
      show: isOpened,
    });
  }

  onNotificationsDropdownToggle() {
    const isOpened = !this.isNotificationsDropdownOpen;
    this.sendEventOnToggle(isOpened);
    this.toggleNotificationsDropdown(isOpened);
  }

  async markUnseenNotifications() {
    try {
      await this.$apollo.mutate<ViewAllNotificationsMutation, ViewAllNotificationsMutationVariables>({
        mutation: viewNotifications,
        variables: { input: { platform: WEB_PLATFORM } },
        update: (store: DataProxy) => this.updateCurrentUserLocal(store, { unseenWarRoomNotifications: false }),
      });

      this.$auth.$storage.setState('user', {
        ...this.$auth.user,
        unseenWarRoomNotifications: false,
      });
    } catch (e) {
      this.$notice.error(e);
      this.$sentryUtils.error(e);
    }
  }

  updateCurrentUserLocal(store: DataProxy, payload: Record<string, boolean | string>): void {
    const userData = store.readQuery<{ currentUser: CurrentUser }>({
      query: currentUserUnseenNotificationsQuery,
      variables: { platform: WEB_PLATFORM },
    });

    const newUserData = {
      ...userData,
      currentUser: {
        ...userData?.currentUser,
        ...payload,
      },
    };

    store.writeQuery({
      query: currentUserUnseenNotificationsQuery,
      data: newUserData,
      variables: { platform: WEB_PLATFORM },
    });
  }
}
